import type { ReactNode } from 'react';
import { AlertIcon, InfoIcon, ShieldIcon, XIcon } from '../icons';
import { IconButton } from './Button';
import './Callout.css';

export type CalloutKind = 'info' | 'warning' | 'legal' | 'danger';

export interface CalloutProps {
  kind?: CalloutKind;
  title?: ReactNode;
  children: ReactNode;
  /** Buttons/links under the text. */
  actions?: ReactNode;
  /** Show a close button; the caller persists the dismissal. */
  onDismiss?: (() => void) | undefined;
  className?: string | undefined;
  role?: 'status' | 'alert' | 'note' | undefined;
}

function iconFor(kind: CalloutKind) {
  switch (kind) {
    case 'info':
      return InfoIcon;
    case 'warning':
      return AlertIcon;
    case 'legal':
      return ShieldIcon;
    case 'danger':
      return AlertIcon;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unknown callout kind ${String(exhaustive)}`);
    }
  }
}

/** Tinted explanatory box: info (blue), warning (gold), legal (blue, shield), danger (red). */
export function Callout({ kind = 'info', title, children, actions, onDismiss, className, role }: CalloutProps) {
  const Icon = iconFor(kind);
  return (
    <div className={['callout', `callout-${kind}`, className ?? ''].filter(Boolean).join(' ')} role={role ?? (kind === 'danger' ? 'alert' : 'note')}>
      <Icon size={20} className="callout-icon" />
      <div className="callout-body">
        {title ? <div className="callout-title">{title}</div> : null}
        <div className="callout-text">{children}</div>
        {actions ? <div className="callout-actions">{actions}</div> : null}
      </div>
      {onDismiss ? <IconButton icon={XIcon} size="s" aria-label="Stäng" tooltip={false} onClick={onDismiss} className="callout-close" /> : null}
    </div>
  );
}

/** Full-width banner (gold by default) for persistent notices like "inte säkerhetskopierad". */
export function Banner({ kind = 'warning', children, actions, onDismiss, className }: Omit<CalloutProps, 'title' | 'role'>) {
  return (
    <div className={['banner', `callout-${kind}`, className ?? ''].filter(Boolean).join(' ')} role="status">
      <div className="banner-text">{children}</div>
      {actions ? <div className="banner-actions">{actions}</div> : null}
      {onDismiss ? <IconButton icon={XIcon} size="s" aria-label="Stäng" tooltip={false} onClick={onDismiss} /> : null}
    </div>
  );
}
